import {BaseElementAsync, OSkinableInit} from "back/commons/basis";
import {IInfo, IInfoBroker, IInfoConsumer} from "lib/commons/infos";
import {IReg, REG} from 'lib/commons/registry';
import {SEC} from 'lib/commons/security';
import {IView, IViewContainer, OViewVisitOptions, VIEWS} from "lib/commons/views";
import {JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {JSrcFields, SRC, srcRef} from "lib/wsp/src";
import {IWspUiEnv, JWspUriChangeMsg, WSP, Wsp} from "lib/wsp/wsp";
import {IShortDescCtx, ITEM} from "lib/wsp/item";
//Force le chargement des ribons.
import "back/wsp/widgets/ribbon/ribbon";
import {EventMgr} from "lib/commons/events";
import {EWspChangesEvts, WspsLivePlace} from "lib/wsp/wspsLive";
import {IItemUiEnv, newSrcView} from "back/wsp/views/itemMain";
import "back/core/zones/webZones";

/**
 * Composant de base pour les mainViews des items.
 */
export interface ItemIsolated {
	initialize(init: OSrcIsolatedInit): this;
}

export interface OSrcIsolatedInit extends OSkinableInit {
	/** Registre de l'atelier. */
	reg: IReg<IWspUiEnv>;
	/** Sourec à afficher */
	srcRef: srcRef;
}

export class ItemIsolated extends BaseElementAsync implements IInfoConsumer, IView, IViewContainer, IShortDescCtx {

	reg: IReg<IItemUiEnv>;
	wsp: Wsp;
	infoBroker: IInfoBroker;
	place: WspsLivePlace;
	view: IView;

	async _initialize(init: OSrcIsolatedInit) {
		this.wsp = init.reg.env.wsp;
		this.infoBroker = init.reg.env.infoBroker;
		if (this.infoBroker) this.infoBroker.addConsumer(this); //on s'abonne de suite pour ne pas louper des demandes de type serchText/searchItemRef, etc.
		const longDesc = await this.wsp.fetchLongDesc(init.srcRef);
		const itemType = this.wsp.wspMetaUi.getItemType(longDesc.itModel);
		this.reg = REG.createSubRegMixed(init.reg, itemType.reg);
		this.reg.env.place = this.wsp.newPlaceWsp();
		//console.log("longDesc:::", longDesc);
		this.reg.env.longDesc = longDesc;
		this.shortDescs[0] = longDesc;
		this.reg.env.srcView = this;
		this.reg.env.longDescChange = new EventMgr();
		this.reg.env.securityCtx = SEC.createSub(init.reg.env.securityCtx, longDesc.srcRoles, longDesc.srcRi, longDesc);

		this.reg.env.place.eventsMgr.on("wspUriChange", this.onWspUriChange.bind(this));

		if (ITEM.isExtItem(longDesc.srcUri)) {
			//On écoute les modifs de cet atelier étranger public.
			this.reg.env.place.getWsp(WSP.extractWspCdFromWspRef(longDesc.itFullUriInOwnerWsp));
		}

		return this._buildContent(init);
	}

	protected async _buildContent(init: OSrcIsolatedInit): Promise<void> {
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:page", this.shadowRoot);
		this.reg.installSkin("wsp-itemisolated", this.shadowRoot);
		this._initAndInstallSkin(this.localName, init);
		const view = await newSrcView(this.reg.env.itemType.getIsolatedView(this.reg.env.longDesc), this.reg);
		if (view) this.shadowRoot.appendChild(view);
	}

	viewShown: boolean;

	onViewShown() {
		this.viewShown = true;
		if (this.view) VIEWS.onViewShown(this.view);
	}

	onViewHidden(closed: boolean) {
		this.viewShown = false;
		if (closed) {
			if (this.reg) {
				this.reg.env.place.closePlace();
				this.reg.close();
			}
			if (this.infoBroker) this.infoBroker.removeConsumer(this);
		}
		if (this.view) VIEWS.onViewHidden(this.view, closed);
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (this.view) {
			const r = visitor(this.view);
			if (r !== undefined) return r;
		}
	}

	async visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (this.view) {
			const r = await visitor(this.view);
			if (r !== undefined) return r;
		}
	}

	onInfo(info: IInfo): void {}

	async onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (msg.wspCd !== this.reg.env.wsp.code) return;
		const longDesc = this.reg.env.longDesc;
		if (SRC.srcRef(longDesc) === SRC.srcRef(msg) || (msg.type === EWspChangesEvts.perm && SRC.isSubUriOrEqual(msg.srcUri, longDesc.srcUri))) {
			const ldNew = await this.reg.env.wsp.fetchLongDesc(SRC.srcRef(longDesc), this);
			if (!this.isConnected) return;
			if (!this.wsp.isAvailable) await this.wsp.waitForAvailable(this);
			this.reg.env.longDesc = ldNew;
			this.shortDescs[0] = ldNew;
			//console.log("longDesc new:::", longDesc);
			this.reg.env.securityCtx = SEC.createSub(this.reg.env.securityCtx, ldNew.srcRoles, ldNew.srcRi, ldNew);
			this.reg.env.longDescChange.emit(ldNew, longDesc);
		}
	}

	//*** Apis IShortDescCtx ***
	shortDescs: JSrcFields[] = [];

	get emitter() {return this}
}

REG.reg.registerSkin('wsp-itemisolated', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		background-color: var(--bgcolor);
		color: var(--color);
	}

	[hidden] {
		display: none !important;
	}
`);

customElements.define('wsp-itemisolated', ItemIsolated);